import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Minus, Plus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import LabelArt from "@/components/LabelArt";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Accordion = ({ title, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-line">
      <button data-testid={testid} onClick={() => setOpen(!open)} className="flex w-full items-center justify-between py-5 text-left">
        <span className="text-[11px] font-medium uppercase tracking-[0.25em]">{title}</span>
        <ChevronDown size={15} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-6 text-sm leading-relaxed text-stone2">{children}</div>}
    </div>
  );
};

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add, openCart } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null); setNotFound(false); setQty(1);
    axios.get(`${API}/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 bg-ivory">
      <p className="font-serif text-3xl font-light italic">This candle has burned out.</p>
      <Link to="/shop" className="btn-outline" data-testid="product-not-found-shop-link">Back to Shop</Link>
    </div>
  );
  if (!product) return <div className="min-h-[60vh] bg-ivory" data-testid="product-loading" />;

  const handleAdd = () => { add(product.slug, qty); toast.success(`${product.name} added to your bag`); openCart(); };
  const handleBuyNow = () => { add(product.slug, qty); navigate("/cart"); };

  return (
    <div data-testid="product-page" className="bg-ivory">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20 py-10 md:py-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone2">
          <Link to="/shop" className="hover:text-ink transition-colors" data-testid="breadcrumb-shop">Shop</Link> / {product.collections[0]} / {product.name}
        </p>
        <div className="mt-10 grid gap-14 lg:grid-cols-2">
          <Reveal className="lg:sticky lg:top-28 self-start">
            <div className="border border-line bg-white p-3">
              <div className="aspect-[4/5] overflow-hidden">
                {product.image
                  ? <img src={product.image} alt={`${product.name} soy wax candle`} data-testid="product-image" className="h-full w-full object-cover" />
                  : <LabelArt name={product.name} size={product.size} />}
              </div>
            </div>
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-stone2">Hand-Poured in Manayunk With Love</p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="eyebrow" data-testid="product-collection">{product.collections.join(" · ")}</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-light tracking-tight" data-testid="product-name">{product.name}</h1>
            <p className="mt-2 font-serif text-xl italic font-light text-stone2" data-testid="product-scent">{product.scent}</p>
            <p className="mt-6 font-serif text-3xl font-light" data-testid="product-price">${product.price.toFixed(2)}</p>

            <div className="mt-8 grid grid-cols-3 gap-px border border-line bg-line">
              {[["Wax", "100% Soy"], ["Size", product.size], ["Net Wt.", product.net_weight]].map(([k, v]) => (
                <div key={k} className="bg-white px-4 py-4 text-center">
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-stone2">{k}</p>
                  <p className="mt-1 text-xs font-medium">{v}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 border border-line bg-white p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone2">Fragrance Notes</p>
              <div className="mt-4 space-y-3">
                {[["Top", product.notes.top], ["Heart", product.notes.heart], ["Base", product.notes.base]].map(([k, v]) => (
                  <div key={k} className="flex items-baseline gap-4" data-testid={`note-${k.toLowerCase()}`}>
                    <span className="w-14 font-mono text-[10px] uppercase tracking-[0.25em] text-stone2">{k}</span>
                    <span className="font-serif text-lg font-light">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className="mt-8 text-sm md:text-base leading-relaxed text-stone2" data-testid="product-description">{product.description}</p>
            <p className="mt-3 text-xs text-stone2">Estimated burn time: {product.burn_time}.</p>

            <div className="mt-10 flex flex-wrap items-stretch gap-4">
              <div className="flex items-center border border-ink">
                <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3.5 hover:bg-cream transition-colors"><Minus size={14} /></button>
                <span data-testid="qty-value" className="w-10 text-center text-sm font-medium">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="px-4 py-3.5 hover:bg-cream transition-colors"><Plus size={14} /></button>
              </div>
              <button data-testid="add-to-cart-button" onClick={handleAdd} className="btn-outline flex-1 min-w-[160px]">Add to Cart</button>
              <button data-testid="buy-now-button" onClick={handleBuyNow} className="btn-primary flex-1 min-w-[160px]">Buy Now</button>
            </div>

            <div className="mt-10">
              <Accordion title="Wax & Wick Details" testid="accordion-wax">Hand-poured 100% soy wax with a lead-free cotton wick, in a clear glass vessel with a gold lid. Burn until the melt pool reaches the edge on first light.</Accordion>
              <Accordion title="Shipping & Returns" testid="accordion-shipping">Orders ship within 3-5 business days. Unburned candles may be returned within 14 days. See our policies in the footer for full details.</Accordion>
              <Accordion title="Vessel Reuse Idea" testid="accordion-vessel">When your candle is finished, rinse the vessel with warm soapy water and give it a second life — a vase, a pen cup, or a home for something small and lovely.</Accordion>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
